/**
 * Parses a configured domain/origin value into a normalized origin string.
 * Accepts either a full URL or a bare host/domain and applies the current
 * protocol for the latter.
 *
 * @param {string} rawDomain Raw domain value from API/config.
 * @returns {string} Normalized origin, or empty string when invalid.
 */
export const parseOriginFromDomain = rawDomain => {
  const value = String(rawDomain || '').trim();
  if (!value) {
    return '';
  }

  try {
    return new URL(value).origin;
  } catch (_) {
    try {
      return new URL(`${window.location.protocol}//${value}`).origin;
    } catch (_) {
      return '';
    }
  }
};

/**
 * Resolves environment config from compile-time globals with runtime fallback.
 *
 * @returns {string} Environment name (e.g. local, development, QA, production).
 */
const getEnvironmentName = () => {
  if (typeof ENVIRONMENT !== 'undefined') {
    return ENVIRONMENT;
  }

  return globalThis.ENVIRONMENT || '';
};

/**
 * Resolves the configured Cognito callback URL from compile-time globals with
 * runtime fallback used by tests.
 *
 * @returns {string} Callback URL string, or empty when unavailable.
 */
const getCognitoCallbackURL = () => {
  if (typeof COGNITO_CALLBACK_URL !== 'undefined') {
    return COGNITO_CALLBACK_URL;
  }

  return globalThis.COGNITO_CALLBACK_URL || '';
};

const getSSOHandoffOrigins = () => {
  if (typeof SSO_HANDOFF_ORIGINS !== 'undefined') {
    return SSO_HANDOFF_ORIGINS;
  }

  return globalThis.SSO_HANDOFF_ORIGINS || '';
};

/**
 * Builds the set of approved frontend origins for cross-origin SSO handoff.
 * COGNITO_CALLBACK_URL is build-time config and identifies the canonical app
 * origin for the current deployment; current window origin is included for
 * same-origin/local scenarios.
 *
 * @returns {Set<string>} Allowed handoff destination origins.
 */
export const getApprovedSSOHandoffOrigins = () => {
  const approvedOrigins = new Set();

  if (window?.location?.origin) {
    approvedOrigins.add(window.location.origin);
  }

  const callbackOrigin = parseOriginFromDomain(getCognitoCallbackURL());
  if (callbackOrigin) {
    approvedOrigins.add(callbackOrigin);
  }

  getSSOHandoffOrigins()
    .split(',')
    .map(parseOriginFromDomain)
    .filter(Boolean)
    .forEach(origin => approvedOrigins.add(origin));

  return approvedOrigins;
};

/**
 * Validates whether a handoff destination is safe for redirect.
 *
 * @param {string} origin Candidate destination origin.
 * @returns {boolean} True when origin is approved and protocol is allowed.
 */
export const isValidSSOHandoffOrigin = origin => {
  if (!origin) {
    return false;
  }

  let originURL;
  try {
    originURL = new URL(origin);
  } catch (_) {
    return false;
  }

  const isLocalEnv = getEnvironmentName() === 'local';
  const isHTTPS = originURL.protocol === 'https:';
  const isLocalhostFamily =
    originURL.hostname === 'localhost' || originURL.hostname.endsWith('.localhost');
  const isLocalHTTP = isLocalEnv && originURL.protocol === 'http:' && isLocalhostFamily;

  if (!isHTTPS && !isLocalHTTP) {
    return false;
  }

  // Local development may legitimately hand off between localhost aliases
  // (for example localhost <-> cobrand.localhost) that are not in build-time
  // callback config. Restrict this relaxation to local env + http localhost family.
  if (isLocalHTTP) {
    return true;
  }

  return getApprovedSSOHandoffOrigins().has(originURL.origin);
};
